import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auditLogApi } from '../../services/auditLogApi';
import { AcceptanceDataPanel } from './AcceptanceDataPanel';
import { AcceptanceInlineFieldHints } from './AcceptanceInlineFieldHints';
import { AcceptanceToolbar } from './AcceptanceToolbar';
import { AcceptanceManifest, AcceptanceRuntimeFieldCoverage } from './types';
import { useAcceptanceMode } from './AcceptanceModeContext';
import { mergeAcceptanceRuntimeFieldCoverage } from './runtimeFieldCoverage';

interface Props<T> {
  manifest: AcceptanceManifest;
  records: T[];
  selectedKeys: string[];
  keyOf: (record: T) => string;
  queryExact: (key: string) => Promise<unknown>;
  lastApiResponse: unknown;
  loading?: boolean;
  onAutoFill: () => void;
  autoFillDisabled?: boolean;
  autoFillReason?: string;
  screenDraft?: unknown;
  fieldCoverageMode?: 'owned_form' | 'shared_document_action';
}

const firstRecord = (value: unknown): unknown => {
  if (!value || typeof value !== 'object') return value;
  const items = (value as { items?: unknown[] }).items;
  if (Array.isArray(items)) return items[0] || null;
  const data = (value as { data?: unknown[] }).data;
  if (Array.isArray(data)) return data[0] || null;
  return value;
};

export function BatchAcceptanceBridge<T>({
  manifest,
  records,
  selectedKeys,
  keyOf,
  queryExact,
  lastApiResponse,
  loading = false,
  onAutoFill,
  autoFillDisabled,
  autoFillReason,
  screenDraft,
  fieldCoverageMode = 'owned_form',
}: Props<T>) {
  const acceptanceMode = useAcceptanceMode();
  const enabled = acceptanceMode.isEnabled(manifest.formId);
  const documentKey = selectedKeys[0] || (records[0] ? keyOf(records[0]) : undefined);
  const [databaseRecord, setDatabaseRecord] = useState<unknown>(null);
  const [auditEntries, setAuditEntries] = useState<unknown[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [observing, setObserving] = useState(false);
  const [runtimeFieldCoverage, setRuntimeFieldCoverage] = useState<AcceptanceRuntimeFieldCoverage>();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const updateRuntimeFieldCoverage = useCallback((next: AcceptanceRuntimeFieldCoverage) => {
    setRuntimeFieldCoverage(current => {
      const merged = mergeAcceptanceRuntimeFieldCoverage(current, next, manifest.fields);
      if (
        current
        && current.candidateCount === merged.candidateCount
        && current.modeledFields.map(field => field.fieldId).join('|') === merged.modeledFields.map(field => field.fieldId).join('|')
        && current.unmodeledFields.map(field => field.fieldId).join('|') === merged.unmodeledFields.map(field => field.fieldId).join('|')
        && current.unmountedProjectedFieldIds.join('|') === merged.unmountedProjectedFieldIds.join('|')
        && current.duplicateProjectedFieldIds.join('|') === merged.duplicateProjectedFieldIds.join('|')
        && current.bindings.map(binding => `${binding.bindingId}:${binding.bindingKind}`).join('|')
          === merged.bindings.map(binding => `${binding.bindingId}:${binding.bindingKind}`).join('|')
      ) return current;
      return merged;
    });
  }, [manifest.fields]);

  useEffect(() => {
    setRuntimeFieldCoverage(undefined);
  }, [enabled, manifest.contractHash, manifest.formId]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setObserving(true);
    const [database, audit] = await Promise.allSettled([
      documentKey ? queryExact(documentKey) : Promise.resolve(null),
      auditLogApi.list({
        form_id: manifest.formId,
        doc_key: documentKey,
        page: 1,
        pageSize: 20,
      }),
    ]);
    setDatabaseRecord(database.status === 'fulfilled' ? firstRecord(database.value) : null);
    setAuditEntries(audit.status === 'fulfilled' ? audit.value.data || [] : []);
    setErrors([
      ...(database.status === 'rejected' ? ['資料庫重新讀取失敗。'] : []),
      ...(audit.status === 'rejected' ? ['SYS_AuditLog 讀取失敗；目前無法確認留痕。'] : []),
    ]);
    setObserving(false);
  }, [documentKey, enabled, manifest.formId, queryExact]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const latestRecords = useMemo(() => records.slice(0, 3), [records]);
  const serializedRuntimeFieldCoverage = useMemo(
    () => runtimeFieldCoverage ? JSON.stringify(runtimeFieldCoverage) : '',
    [runtimeFieldCoverage],
  );
  const serializedMountTriggers = useMemo(
    () => JSON.stringify(
      manifest.fields
        .filter(field => field.conditionallyMounted && field.mountTrigger)
        .map(field => ({
          fieldId: field.fieldId,
          trigger: field.mountTrigger,
        })),
    ),
    [manifest.fields],
  );

  return (
    <>
      <span ref={anchorRef} data-acceptance-inline-hints-anchor={manifest.formId} style={{ display: 'none' }} />
      <AcceptanceInlineFieldHints
        manifest={manifest}
        documentKey={documentKey}
        anchorRef={anchorRef}
        onCoverageChange={updateRuntimeFieldCoverage}
        includeUnmodeledFields={fieldCoverageMode === 'owned_form'}
      />
      <div
        data-acceptance-surface={manifest.formId}
        data-acceptance-runtime-field-coverage={serializedRuntimeFieldCoverage}
        data-acceptance-mount-triggers={serializedMountTriggers}
      >
        <AcceptanceToolbar
          manifest={manifest}
          onAutoFill={onAutoFill}
          documentKey={documentKey}
          autoFillDisabled={autoFillDisabled}
          autoFillReason={autoFillReason}
        />
        <AcceptanceDataPanel
          manifest={manifest}
          documentKey={documentKey}
          snapshots={{
            screenDraft: screenDraft || {
              selected_items: selectedKeys,
              candidates: records,
            },
            lastApiResponse,
            databaseRecord,
          }}
          latestRecords={latestRecords}
          history={[]}
          auditEntries={auditEntries}
          loading={loading || observing}
          onRefresh={() => { void refresh(); }}
          observationErrors={errors}
          runtimeFieldCoverage={runtimeFieldCoverage}
        />
      </div>
    </>
  );
}
